/**
 * Feed card (PRD Feed mode, §9.3).
 *
 * LinkedIn-style, as a kit island: author header, type tags, the post text, the media area
 * (placeholder + "Load media" when the global toggle is off, lazy IntersectionObserver mount when
 * it is on), the embedded original for reposts/quotes, then the metric row — glyph, number and the
 * metric's own multiplier chip in one line.
 *
 * The card itself opens the post-preview popup: a plain click anywhere that is not an interactive
 * descendant, or Enter on the focused card. The footer carries the same six actions the popup's
 * bar does, in the same order — "Open on LinkedIn" labelled, the other five icon-only with their
 * names on hover.
 *
 * The author header links **in-app** (FEED_SPEC): name and avatar go to `/view/profile/<id>`, which
 * lands on that profile's persisted mode. LinkedIn stays reachable through the footer action, and
 * the headline is text — one link per row of the header, not three.
 */

import type { MouseEvent, ReactNode } from 'react';
import { Link } from 'react-router';
import type { FeedCardDTO } from '@lw/shared';
import {
  Avatar,
  BookmarkFilledIcon,
  BookmarkIcon,
  CheckIcon,
  CopyIcon,
  ExternalLinkIcon,
  IconAction,
  LinkIcon,
  NotionIcon,
  SparkleIcon,
  Spinner,
  cx,
} from '../../../components';
import { postDeepLink, viewPath } from '../../../app/routes';
import { useCommentChat } from '../../ai/CommentChatProvider';
import { useNotionSave } from '../../notion/useNotionSave';
import { useSaveDialog } from '../../saved/SaveDialog';
import { SavedRowsBlock } from '../../saved/SavedRowsBlock';
import { useCopyToClipboard } from '../../../lib/hooks';
import { isPlainCardClick } from '../../../lib/plainClick';
import {
  CappedArea,
  EditedChip,
  EmbeddedOriginalBlock,
  GroupLine,
  MetricsRow,
  PossiblyDeletedMarker,
  PostComponentBlock,
  PostMediaArea,
  PostText,
  PostTypeBadges,
  RepostedLine,
  UnmappedBadge,
} from '../postRender';

export type FeedCardProps = {
  card: FeedCardDTO;
  mediaEnabled: boolean;
  onOpen: (postId: number) => void;
  /**
   * Notification variant (SPEC `notifications`): a quiet reason row above the card body. The card
   * is parameterized rather than forked, so the feed and the alert surfaces can never drift apart.
   */
  banner?: ReactNode;
  /** Prepended to the footer, before the icon actions — the alert's primary "Mark As Read". */
  footerLead?: ReactNode;
  /** Trailing footer text; the read date on an already-read alert. */
  footerNote?: ReactNode;
  /** Drops the "Open on LinkedIn" label — the narrower alert card has no room for it. */
  compactActions?: boolean;
  /**
   * `/watch` variant: leads the footer with a "Show posts" link to the author's profile and demotes
   * "Open on LinkedIn" to the same ghost text style. Posts on this screen are minutes old, so the
   * profile page is the safe way in and the permalink stops being the loud default.
   */
  watchActions?: boolean;
  /** Caps the media area at N px behind a fade + "View Full" (measured, never guessed). */
  mediaCapPx?: number;
  className?: string;
};

export function FeedCard({
  card,
  mediaEnabled,
  onOpen,
  banner,
  footerLead,
  footerNote,
  compactActions = false,
  watchActions = false,
  mediaCapPx,
  className,
}: FeedCardProps) {
  const isRepost = card.postType === 'repost';
  const isQuote = card.postType === 'repost_with_comment';
  // No `?mode=`: the profile's own persisted mode wins, exactly as a homescreen row link does.
  const authorPath = viewPath('profile', card.author.id);
  // A pure repost has no metrics of its own: every cell renders the server's "—" (§7.4).
  const metricsRow = <MetricsRow metrics={card.metrics} multipliers={card.multipliers} />;
  // Two instances so the two confirmations never flip together, exactly as the popup does.
  const [copied, copy] = useCopyToClipboard();
  const [linkCopied, copyLink] = useCopyToClipboard();
  // Keyed by post id above both surfaces, so the same post spins here and in the popup at once.
  const notion = useNotionSave();
  const saving = notion.isSaving(card.id);
  // Always opens the popup, saved or not: the popup *is* the edit form and the unsave confirm, so
  // a bookmark press can never silently destroy a note someone wrote (SPEC `saved-posts`).
  const { openSave } = useSaveDialog();
  // Same shape again: the chat popup is hosted at the root, so the card only names a post id.
  const { openCommentChat } = useCommentChat();

  const openIfPlainClick = (event: MouseEvent<HTMLElement>) => {
    if (isPlainCardClick(event)) onOpen(card.id);
  };

  const media =
    mediaCapPx === undefined ? (
      <PostMediaArea media={card.media} mediaEnabled={mediaEnabled} />
    ) : (
      <CappedArea capPx={mediaCapPx} actionLabel="View Full" onAction={() => onOpen(card.id)}>
        <PostMediaArea media={card.media} mediaEnabled={mediaEnabled} />
      </CappedArea>
    );

  return (
    <article
      className={cx('card post-card post-card--clickable', className)}
      tabIndex={0}
      onClick={openIfPlainClick}
      onKeyDown={(event) => {
        // Enter on the card itself; Space keeps scrolling the feed, and a key pressed on a focused
        // link or button inside belongs to that control.
        if (event.key === 'Enter' && event.target === event.currentTarget) onOpen(card.id);
      }}
    >
      {banner}

      <header className="post-card__head">
        {/* The avatar is a second, unlabelled path to the same screen — hidden from AT so the
            header still reads as one link. */}
        <Link
          className="post-card__avatar-link"
          to={authorPath}
          tabIndex={-1}
          aria-hidden="true"
        >
          <Avatar
            media={card.author.avatar}
            name={card.author.name}
            mediaEnabled={mediaEnabled}
            kind={card.author.kind}
          />
        </Link>
        <div className="post-card__author">
          <Link className="post-card__name" to={authorPath}>
            {card.author.name ?? card.author.slug}
          </Link>
          {card.author.headline !== null ? (
            <span className="muted post-card__headline">{card.author.headline}</span>
          ) : null}
          <span className="faint">{card.publishedAtDisplay}</span>
        </div>
        <div className="post-card__flags">
          <PostTypeBadges
            postType={card.postType}
            contentType={card.contentType}
            typeDisplay={card.typeDisplay}
          />
          <EditedChip edited={card.edited} />
          <PossiblyDeletedMarker possiblyDeleted={card.possiblyDeleted} />
          <UnmappedBadge keys={card.unmappedKeys} />
        </div>
      </header>

      {/* Lists, tags, note and the saved date — on every surface, not just `/saved` (§12.5). */}
      {card.savedInfo === null ? null : (
        <SavedRowsBlock info={card.savedInfo} onEdit={() => openSave(card.id)} />
      )}

      <RepostedLine postType={card.postType} />
      <GroupLine group={card.group} />
      <PostText text={card.text} />
      {media}
      {/* Shape A renders its typed component nested in the embedded frame — it is the original's
          video/document/poll, not the reposter's. */}
      {isRepost ? null : (
        <PostComponentBlock component={card.component} mediaEnabled={mediaEnabled} />
      )}
      {/* A quote post's own metrics sit above the card it quotes; every other post keeps them last. */}
      {isQuote ? metricsRow : null}
      <EmbeddedOriginalBlock
        embedded={card.embedded}
        mediaEnabled={mediaEnabled}
        onOpenPost={onOpen}
        component={isRepost ? card.component : null}
      />
      {isQuote ? null : metricsRow}

      <footer className="post-card__foot">
        {footerLead}
        {watchActions ? (
          <IconAction
            label="Show posts"
            href={card.author.url}
            icon={<ExternalLinkIcon size={14} />}
            showLabel
          />
        ) : null}
        {card.url === null ? null : (
          <IconAction
            label="Open on LinkedIn"
            href={card.url}
            icon={<ExternalLinkIcon size={14} />}
            showLabel={!compactActions && !watchActions}
          />
        )}
        <IconAction
          label={card.saved ? 'Saved' : 'Save'}
          icon={card.saved ? <BookmarkFilledIcon size={14} /> : <BookmarkIcon size={14} />}
          onClick={() => openSave(card.id)}
        />
        <IconAction
          label={saving ? 'Saving to Notion…' : 'Save to Notion'}
          icon={saving ? <Spinner size="sm" /> : <NotionIcon size={14} />}
          disabled={saving}
          onClick={() => {
            notion.save(card.id);
          }}
        />
        <IconAction
          label="Generate comment"
          icon={<SparkleIcon size={14} />}
          onClick={() => openCommentChat(card.id)}
        />
        <IconAction
          label={linkCopied ? 'Copied' : 'Copy link'}
          icon={linkCopied ? <CheckIcon size={14} /> : <LinkIcon size={14} />}
          onClick={() => copyLink(postDeepLink(card.id, card.author.id))}
        />
        <IconAction
          label={copied ? 'Copied' : 'Copy text'}
          icon={copied ? <CheckIcon size={14} /> : <CopyIcon size={14} />}
          disabled={card.text === null}
          onClick={() => copy(card.text ?? '')}
        />
        {footerNote}
      </footer>
    </article>
  );
}

export default FeedCard;
